import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { itineraryApi } from '../services/api';
import { toast } from 'sonner';
import {
  Globe, Loader2, ExternalLink, AlertTriangle, Search, ArrowLeft, FileCheck
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function VisaCheckPage() {
  const [nationality, setNationality] = useState('');
  const [destination, setDestination] = useState('');
  const [loading, setLoading] = useState(false);
  const [visaData, setVisaData] = useState(null);
  const navigate = useNavigate();

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!nationality.trim() || !destination.trim()) {
      toast.error('Please enter both nationality and destination');
      return;
    }
    setLoading(true);
    setVisaData(null);
    try {
      const res = await itineraryApi.getVisaInfo(nationality.trim(), destination.trim());
      if (res.data.success) {
        setVisaData(res.data.visa_info);
      } else {
        toast.error(res.data.error || 'Failed to fetch visa info');
      }
    } catch {
      toast.error('Failed to check visa requirements. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
      <Header />

      <main className="pt-24 pb-20 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate(-1)} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-btn-visa">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>

          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-white font-bold" data-testid="visa-check-title">
              Visa Check
            </h1>
            <p className="text-white/60 text-sm mt-3 max-w-lg mx-auto">
              Check visa requirements for your destination based on your nationality.
            </p>
          </motion.div>

          {/* Search form */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-white/95 backdrop-blur-xl border border-white/30 shadow-xl">
              <CardContent className="p-6">
                <form onSubmit={handleCheck} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-ocean mb-1.5 block">Your Nationality</label>
                      <Input
                        placeholder="e.g. Indian, American, British"
                        value={nationality}
                        onChange={e => setNationality(e.target.value)}
                        className="bg-white border-ocean/20 focus:border-ocean text-charcoal"
                        data-testid="visa-nationality-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-ocean mb-1.5 block">Country to Visit</label>
                      <Input
                        placeholder="e.g. Japan, France, USA"
                        value={destination}
                        onChange={e => setDestination(e.target.value)}
                        className="bg-white border-ocean/20 focus:border-ocean text-charcoal"
                        data-testid="visa-destination-input"
                      />
                    </div>
                  </div>
                  <div className="flex justify-center pt-2">
                    <Button
                      type="submit"
                      disabled={loading || !nationality.trim() || !destination.trim()}
                      className="bg-ocean hover:bg-ocean-light text-white rounded-full px-8 py-2.5 font-semibold shadow-lg"
                      data-testid="visa-check-submit"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                      Check Visa Requirements
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Loading state */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-gold mb-4" />
              <p className="text-white/70 text-sm">Checking visa requirements...</p>
            </div>
          )}

          {/* Results */}
          <AnimatePresence>
            {visaData && !loading && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-8 space-y-5"
              >
                {/* Status card */}
                <Card className={`bg-white/95 backdrop-blur-xl border-2 ${visaData.visa_required ? 'border-red-200' : 'border-green-200'}`} data-testid="visa-result-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-5">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${visaData.visa_required ? 'bg-red-100' : 'bg-green-100'}`}>
                        <Globe className={`w-7 h-7 ${visaData.visa_required ? 'text-red-600' : 'text-green-600'}`} />
                      </div>
                      <div>
                        <h3 className="font-serif text-xl text-charcoal">
                          {visaData.visa_required ? 'Visa Required' : 'Visa-Free Entry'}
                        </h3>
                        {visaData.visa_type && <p className="text-ocean text-sm">{visaData.visa_type}</p>}
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {visaData.max_stay && (
                        <div className="bg-sky/5 p-4 rounded-xl">
                          <p className="text-xs text-ocean/60 mb-1">Max Stay</p>
                          <p className="font-semibold text-ocean text-sm">{visaData.max_stay}</p>
                        </div>
                      )}
                      {visaData.processing_time && (
                        <div className="bg-gold/5 p-4 rounded-xl">
                          <p className="text-xs text-ocean/60 mb-1">Processing Time</p>
                          <p className="font-semibold text-ocean text-sm">{visaData.processing_time}</p>
                        </div>
                      )}
                      {visaData.cost && (
                        <div className="bg-burnt/5 p-4 rounded-xl">
                          <p className="text-xs text-ocean/60 mb-1">Visa Cost</p>
                          <p className="font-semibold text-ocean text-sm">{visaData.cost}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Documents */}
                {visaData.documents_required && visaData.documents_required.length > 0 && (
                  <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl">
                    <CardHeader><CardTitle className="font-serif text-lg text-ocean flex items-center gap-2"><FileCheck className="w-5 h-5" /> Required Documents</CardTitle></CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {visaData.documents_required.map((doc, i) => (
                          <li key={`vdoc-${doc.slice(0,20)}-${i}`} className="flex items-start gap-2 text-sm text-charcoal">
                            <span className="text-ocean mt-0.5 flex-shrink-0">&#10003;</span>{doc}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Apply link */}
                {visaData.application_url && visaData.application_url !== 'N/A' && visaData.application_url !== 'n/a' && !visaData.application_url.includes('N/A') && (
                  <Card className="bg-white/95 backdrop-blur-xl border-ocean/20 shadow-lg" data-testid="visa-apply-card">
                    <CardContent className="p-6 flex items-center justify-between gap-4">
                      <div>
                        <h4 className="font-semibold text-ocean">Apply for Visa Online</h4>
                        <p className="text-amber-600 text-xs mt-1">AI-suggested link — please verify with official government sources before applying</p>
                      </div>
                      <a href={visaData.application_url} target="_blank" rel="noopener noreferrer">
                        <Button className="bg-ocean hover:bg-ocean-light text-white rounded-full flex-shrink-0" data-testid="visa-apply-btn">
                          <ExternalLink className="w-4 h-4 mr-2" /> Visit Link
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                )}

                {/* Notes */}
                {visaData.notes && (
                  <Card className="bg-amber-50/80 border-amber-200">
                    <CardContent className="p-5">
                      <p className="text-sm text-amber-800 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />{visaData.notes}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
